import { useState } from "react";
import { faqItems } from "@/mocks/categories";

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [currentFaq, setCurrentFaq] = useState(0);

  const toggleFaq = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const handlePrev = () => {
    setCurrentFaq((prev) => (prev > 0 ? prev - 1 : faqItems.length - 1));
    setOpenIndex(currentFaq > 0 ? currentFaq - 1 : faqItems.length - 1);
  };

  const handleNext = () => {
    setCurrentFaq((prev) => (prev < faqItems.length - 1 ? prev + 1 : 0));
    setOpenIndex(currentFaq < faqItems.length - 1 ? currentFaq + 1 : 0);
  };

  return (
    <section id="faq" className="relative bg-background-50 py-16 md:py-24 px-6 md:px-10">
      <div className="max-w-3xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-medium text-foreground-600 bg-background-100 rounded-full mb-4">
            <i className="ri-star-fill text-accent-500 text-xs"></i>
            4.9/5
          </span>
          <h2 className="font-heading text-3xl md:text-4xl text-foreground-900 font-medium mb-4">
            Perguntas Frequentes
          </h2>
          <p className="text-foreground-600 text-sm max-w-lg mx-auto">
            Respostas às dúvidas mais comuns sobre a Hoobuy Spreadsheet
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3 mb-8">
          {faqItems.map((item, idx) => (
            <div
              key={idx}
              className={`rounded-2xl border transition-all ${
                openIndex === idx
                  ? "bg-background-100 border-background-200"
                  : "bg-transparent border-background-200/40 hover:border-background-200"
              }`}
            >
              <button
                onClick={() => toggleFaq(idx)}
                className="w-full flex items-center justify-between p-5 md:p-6 text-left cursor-pointer"
              >
                <h3 className="font-heading text-base md:text-lg text-foreground-900 font-medium pr-4">
                  {item.question}
                </h3>
                <i
                  className={`ri-arrow-down-s-line text-xl text-foreground-500 transition-transform duration-300 flex-shrink-0 ${
                    openIndex === idx ? "rotate-180" : ""
                  }`}
                ></i>
              </button>
              {openIndex === idx && (
                <div className="px-5 md:px-6 pb-5 md:pb-6">
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Navigation & Author */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-background-200 overflow-hidden flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20professional%20team%20avatar%20portrait%2C%20warm%20neutral%20background%2C%20soft%20lighting%2C%20European%20aesthetic&width=80&height=80&seq=hoobuy-team-avatar&orientation=squarish"
                alt="Equipe Hoobuy Life"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-900">Equipe Hoobuy Life</p>
              <p className="text-xs text-foreground-500">suporte@hoobuy.life</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="FAQ anterior"
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <button
              onClick={handleNext}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-foreground-900 text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer"
              aria-label="Próximo FAQ"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>

      {/* FAQ Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems.map((item) => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer,
              },
            })),
          }),
        }}
      />
    </section>
  );
}